import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listasComprasApi } from '../../api/endpoints'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card, CardHeader, ProgressBar } from '../../components/ui/Card'
import { EmptyState, LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { cn } from '../../lib/utils'
import { BloqueioPremium } from '../notas/BloqueioPremium'
import { usePermiteNotas } from '../notas/usePermiteNotas'
import { ListaDetalhe } from './ListaDetalhe'

/** Gestão das listas de compras: as minhas e as compartilhadas comigo, com edição na tela grande. */
export function ListasPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const { permite } = usePermiteNotas()
  const [criando, setCriando] = useState(false)
  const [nome, setNome] = useState('')

  const { data: listas = [], isLoading } = useQuery({ queryKey: ['listas-compras'], queryFn: listasComprasApi.list, enabled: permite })
  const selecionada = id ?? listas.find((l) => l.status === 1)?.id ?? listas[0]?.id

  const criar = useMutation({
    mutationFn: () => listasComprasApi.criar(nome.trim()),
    onSuccess: (l) => {
      qc.invalidateQueries({ queryKey: ['listas-compras'] })
      toast.success('Lista criada.')
      setCriando(false); setNome('')
      navigate(`/listas/${l.id}`)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <PageHeader title="Listas de compras" />
      {!permite ? <BloqueioPremium recurso="A lista de compras" /> : (
        <div className="grid items-start gap-6 lg:grid-cols-[20rem_1fr]">
          <Card>
            <CardHeader title="Minhas listas" icon="ri-shopping-cart-2-line" actions={<Button data-tutorial="lista.nova" size="sm" icon="ri-add-line" onClick={() => setCriando(true)}>Nova</Button>} />
            {isLoading ? <LoadingState /> : listas.length === 0 ? (
              <EmptyState icon="ri-shopping-cart-2-line" message="Você ainda não tem listas." action={<Button data-tutorial="lista.nova" variant="soft-primary" icon="ri-add-line" onClick={() => setCriando(true)}>Criar a primeira</Button>} />
            ) : (
              <div className="space-y-1 p-2">
                {listas.map((l) => (
                  <button
                    key={l.id} type="button" onClick={() => navigate(`/listas/${l.id}`)} aria-current={selecionada === l.id}
                    className={cn('block w-full rounded px-3 py-2.5 text-left transition-colors', selecionada === l.id ? 'bg-primary/10' : 'hover:bg-surface2')}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className={cn('truncate font-medium', selecionada === l.id ? 'text-primary' : 'text-white')}>{l.nome}</span>
                      <span className="flex gap-1">
                        {l.compartilhada && <Badge variant="info">Compartilhada</Badge>}
                        {l.status === 2 && <Badge variant="muted">Concluída</Badge>}
                      </span>
                    </span>
                    <span className="mt-1 block text-2xs text-muted">{l.comprados} de {l.total} itens{l.compartilhada ? ` · de ${l.donoNome}` : ''}</span>
                    <ProgressBar value={l.total ? (l.comprados / l.total) * 100 : 0} tone="income" className="mt-1.5" />
                  </button>
                ))}
              </div>
            )}
          </Card>

          {selecionada ? <ListaDetalhe key={selecionada} listaId={selecionada} aoRemover={() => navigate('/listas')} /> : (
            <Card><EmptyState icon="ri-list-check-2" message="Crie uma lista para começar." /></Card>
          )}
        </div>
      )}

      <Modal
        open={criando} onClose={() => setCriando(false)} title="Nova lista" size="sm"
        footer={<><Button variant="light" onClick={() => setCriando(false)}>Cancelar</Button><Button icon="ri-add-line" disabled={!nome.trim()} loading={criar.isPending} onClick={() => criar.mutate()}>Criar lista</Button></>}
      >
        <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Compra do mês" autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && nome.trim()) criar.mutate() }} />
      </Modal>
    </>
  )
}
